import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { LEGAL_LAST_UPDATED, LEGAL_LAST_UPDATED_AR } from "@/lib/legal";

export const Route = createFileRoute("/legal-notice")({
  head: () => ({
    meta: [
      { title: "Legal Notice — Lumvio" },
      {
        name: "description",
        content: "Publisher information, hosting details, and intellectual property notice for Lumvio.",
      },
      { property: "og:title", content: "Legal Notice — Lumvio" },
      { property: "og:description", content: "Publisher, hosting, and IP notice for Lumvio." },
      { property: "og:url", content: "https://lumvio.co/legal-notice" },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/legal-notice" }],
  }),
  component: LegalNoticePage,
});

function LegalNoticePage() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");
  return (
    <LegalPageLayout title={ar ? "الإشعار القانوني" : "Legal Notice"} updated={ar ? LEGAL_LAST_UPDATED_AR : LEGAL_LAST_UPDATED}>
      {ar ? <ArabicBody /> : <EnglishBody />}
    </LegalPageLayout>
  );
}

function EnglishBody() {
  return (
    <>
      <p>
        This legal notice provides required information about the publisher of the Lumvio website and applications (the
        "Service").
      </p>

      <h2>1. Publisher</h2>
      <p>
        Lumvio — verified freelancer workspace.
        <br />
        Contact: <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>
      </p>

      <h2>2. Publication Director</h2>
      <p>The publication director is the legal representative of Lumvio.</p>

      <h2>3. Hosting</h2>
      <p>
        The Service is hosted on infrastructure operated by Cloudflare, Inc. and Supabase. Static assets and edge
        functions run on the Cloudflare global network.
      </p>

      <h2>4. Intellectual Property</h2>
      <p>
        All elements of the Service — including source code, design, trademarks, logos, and text — are the exclusive
        property of Lumvio or its licensors and are protected by applicable intellectual property laws. Any
        reproduction, representation, modification, or distribution without prior written permission is prohibited.
      </p>

      <h2>5. User Content</h2>
      <p>
        Users retain ownership of content they upload to the Service and grant Lumvio a limited licence to host,
        transmit, and display that content for the purpose of operating the Service.
      </p>

      <h2>6. Liability</h2>
      <p>
        Lumvio makes reasonable efforts to keep the Service available and accurate but does not warrant uninterrupted
        availability or absence of errors. To the extent permitted by law, Lumvio disclaims liability for indirect or
        consequential damages arising from use of the Service.
      </p>

      <h2>7. Applicable Law</h2>
      <p>
        This legal notice is governed by the laws applicable at the publisher's registered seat. Any dispute relating to
        the Service will be submitted to the competent courts, subject to mandatory consumer protection rules where
        applicable.
      </p>

      <h2>8. Reporting</h2>
      <p>
        To report abuse, infringement, or a security issue, email{" "}
        <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <p>
        يقدّم هذا الإشعار القانوني المعلومات المطلوبة عن ناشر موقع Lumvio وتطبيقاته ("الخدمة").
      </p>

      <h2>1. الناشر</h2>
      <p>
        Lumvio — مساحة عمل للمستقلين الموثّقين.
        <br />
        التواصل: <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>
      </p>

      <h2>2. مدير النشر</h2>
      <p>مدير النشر هو الممثل القانوني لـ Lumvio.</p>

      <h2>3. الاستضافة</h2>
      <p>
        تُستضاف الخدمة على بنية تحتية تُشغّلها Cloudflare, Inc. و Supabase. تعمل الأصول الثابتة ووظائف الحافة (Edge)
        على شبكة Cloudflare العالمية.
      </p>

      <h2>4. الملكية الفكرية</h2>
      <p>
        جميع عناصر الخدمة — بما في ذلك الكود المصدري والتصميم والعلامات التجارية والشعارات والنصوص — هي ملكية حصرية
        لـ Lumvio أو مرخّصيها ومحمية بموجب قوانين الملكية الفكرية المعمول بها. يُحظر أي نسخ أو تمثيل أو تعديل أو توزيع
        دون إذن كتابي مسبق.
      </p>

      <h2>5. محتوى المستخدم</h2>
      <p>
        يحتفظ المستخدمون بملكية المحتوى الذي يرفعونه إلى الخدمة ويمنحون Lumvio ترخيصًا محدودًا لاستضافة ذلك المحتوى
        ونقله وعرضه بغرض تشغيل الخدمة.
      </p>

      <h2>6. المسؤولية</h2>
      <p>
        تبذل Lumvio جهودًا معقولة لإبقاء الخدمة متاحة ودقيقة، لكنها لا تضمن استمرارية التوفر أو خلوّها من الأخطاء. إلى
        الحد الذي يسمح به القانون، تُخلي Lumvio مسؤوليتها عن الأضرار غير المباشرة أو التبعية الناشئة عن استخدام
        الخدمة.
      </p>

      <h2>7. القانون المعمول به</h2>
      <p>
        يخضع هذا الإشعار القانوني للقوانين المعمول بها في مقر الناشر المسجّل. يُحال أي نزاع يتعلق بالخدمة إلى المحاكم
        المختصة، مع مراعاة قواعد حماية المستهلك الإلزامية حيثما انطبقت.
      </p>

      <h2>8. الإبلاغ</h2>
      <p>
        للإبلاغ عن سوء الاستخدام أو الانتهاك أو مشكلة أمنية، راسل{" "}
        <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </>
  );
}
